/**
 * Capture Manager for Slang Sandbox
 * Handles screenshot (PNG) and video recording (WebM)
 */

export class CaptureManager {
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.maxFrames = options.maxFrames || 300;
    this.isRecording = false;
    this.frameCount = 0;
    this.mediaRecorder = null;
    this.chunks = [];

    // Callbacks
    this.onRecordingStart = null;
    this.onRecordingStop = null;
    this.onFrameUpdate = null;
  }

  /**
   * Take a screenshot and download as PNG
   */
  async screenshot() {
    try {
      const blob = await new Promise((resolve, reject) => {
        this.canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create screenshot blob'));
          }
        }, 'image/png');
      });

      this.downloadBlob(blob, `shader-${Date.now()}.png`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Start video recording
   * @param {number} maxFrames - Maximum number of frames to record
   */
  startRecording(maxFrames) {
    if (this.isRecording) {
      return { success: false, error: 'Already recording' };
    }

    // Check MediaRecorder support
    if (!this.checkMediaRecorderSupport()) {
      return { success: false, error: 'MediaRecorder is not supported in this browser' };
    }

    this.maxFrames = maxFrames || this.maxFrames;
    this.frameCount = 0;
    this.chunks = [];

    try {
      // Capture stream from canvas at 60fps
      const stream = this.canvas.captureStream(60);

      // Determine supported mimeType
      const mimeType = this.getSupportedMimeType();
      if (!mimeType) {
        return { success: false, error: 'No supported video codec found' };
      }

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 8000000, // 8 Mbps
      });

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          this.chunks.push(e.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.saveRecording();
      };

      this.mediaRecorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        this.isRecording = false;
      };

      this.mediaRecorder.start();
      this.isRecording = true;

      if (this.onRecordingStart) {
        this.onRecordingStart();
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Check MediaRecorder support
   * @returns {boolean}
   */
  checkMediaRecorderSupport() {
    return typeof MediaRecorder !== 'undefined' && typeof this.canvas.captureStream === 'function';
  }

  /**
   * Get supported video mimeType
   * @returns {string|null}
   */
  getSupportedMimeType() {
    const types = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return null;
  }

  /**
   * Called on each frame from render loop
   * @returns {{ recording: boolean, current?: number, max?: number }}
   */
  onFrame() {
    if (!this.isRecording) {
      return { recording: false };
    }

    this.frameCount++;

    if (this.onFrameUpdate) {
      this.onFrameUpdate(this.frameCount, this.maxFrames);
    }

    if (this.frameCount >= this.maxFrames) {
      this.stopRecording();
    }

    return {
      recording: true,
      current: this.frameCount,
      max: this.maxFrames,
    };
  }

  /**
   * Stop recording
   */
  stopRecording() {
    if (!this.isRecording || !this.mediaRecorder) {
      return;
    }

    this.mediaRecorder.stop();
    this.isRecording = false;

    if (this.onRecordingStop) {
      this.onRecordingStop();
    }
  }

  /**
   * Save recorded video
   */
  saveRecording() {
    if (this.chunks.length === 0) {
      console.warn('No recording data to save');
      return;
    }

    const mimeType = this.mediaRecorder?.mimeType || 'video/webm';
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const blob = new Blob(this.chunks, { type: mimeType });

    this.downloadBlob(blob, `shader-${Date.now()}.${extension}`);
    this.chunks = [];
  }

  /**
   * Download a blob as a file
   * @param {Blob} blob
   * @param {string} filename
   */
  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.isRecording) {
      this.stopRecording();
    }
    this.mediaRecorder = null;
    this.chunks = [];
  }
}
